import React from 'react';

import type { StoredUser } from '../lib/api';
import DalurenPanPage from './DalurenPanPage';
import Decoder from './Decoder';
import FindObject from './FindObject';
import KangxiDictionary from './KangxiDictionary';
import KnowledgePK from './KnowledgePK';
import NamingMasterApp from './NamingMasterApp';
import OriginMemoryReader from './OriginMemoryReader';
import PanTestPage from './PanTestPage';
import Quiz from './Quiz';
import SancaiLiuge from './SancaiLiuge';
import StoryReader from './StoryReader';
import TheoryReader from './TheoryReader';
import Trainer from './Trainer';
import WanNianLiApp from './WanNianLiApp';
import WuxingClothing from './WuxingClothing';
import WenceApp from './WenceApp';
import ZhengYuanReportPage from './ZhengYuanReportPage';
import { PSYCH_TOOL_PAGE_MAP } from './psych-pages';

type ArchiveToolId = 'pan' | 'dalurenPan' | 'sancaiLiuge';
interface ArchiveToolState {
  tool: ArchiveToolId;
  pendingRecordId: string | null;
}

interface AppToolRendererProps {
  currentTool: string | null;
  userProfile: StoredUser | null;
  fourXiangyiUnlocked: boolean;
  effectiveMobileDockStyle: 'default' | 'ios';
  memberActive: boolean;
  theoryProAccess: boolean;
  originMemoryProAccess: boolean;
  originMemoryProCategoryUnlocks?: Partial<Record<'doors' | 'stars' | 'gods' | 'trigrams', boolean>>;
  returnToPanFromTool: boolean;
  onGoHome: () => void;
  onSetCurrentTool: (tool: string | null) => void;
  onClearSelectedSymbol: () => void;
  onSetViewMode: (mode: 'dashboard' | 'sidebar_view') => void;
  onSetMobileTab: (tab: 'practice' | 'study' | 'mine') => void;
  onSetReturnToPanFromTool: (value: boolean) => void;
  onReplaceCurrentUrlQuery: (query: URLSearchParams | null, path?: string) => void;
  onEnterToolMode: (tool: string, options?: { fromPan?: boolean; queryParams?: URLSearchParams | null }) => void;
  ensureTrialAvailable: (toolId: string, toolName: string) => Promise<boolean>;
  openProModal: (toolName: string) => void;
  setShowAuthModal: (open: boolean) => void;
  buildIntro: (id: 'trainer' | 'decoder' | 'findObject' | 'quiz' | 'knowledgePK') => string;
  archiveToolState?: ArchiveToolState | null;
  onArchiveBack?: () => void;
  onArchiveRecordConsumed?: (tool: ArchiveToolId, recordId: string) => void;
  /** 象意分类模式：full=保留二级分类，simple=简化 */
  xiangyiCategoryMode?: 'full' | 'simple';
}

const AppToolRenderer: React.FC<AppToolRendererProps> = ({
  currentTool,
  userProfile,
  fourXiangyiUnlocked,
  effectiveMobileDockStyle,
  memberActive,
  theoryProAccess,
  originMemoryProAccess,
  originMemoryProCategoryUnlocks,
  returnToPanFromTool,
  onGoHome,
  onSetCurrentTool,
  onClearSelectedSymbol,
  onSetViewMode,
  onSetMobileTab,
  onSetReturnToPanFromTool,
  onReplaceCurrentUrlQuery,
  onEnterToolMode,
  ensureTrialAvailable,
  openProModal,
  setShowAuthModal,
  buildIntro,
  archiveToolState = null,
  onArchiveBack,
  onArchiveRecordConsumed,
  xiangyiCategoryMode = 'full',
}) => {
  if (!currentTool) return null;

  const isArchiveToolActive = archiveToolState?.tool === currentTool;
  const archivePendingRecordId = isArchiveToolActive ? archiveToolState?.pendingRecordId || null : null;

  const handleBack = () => {
    if (isArchiveToolActive && onArchiveBack) {
      onArchiveBack();
      return;
    }
    onGoHome();
  };
  const handlePracticeBack = () => {
    if (returnToPanFromTool) {
      onSetCurrentTool('pan');
      onClearSelectedSymbol();
      onSetViewMode('sidebar_view');
      onSetReturnToPanFromTool(false);
      onReplaceCurrentUrlQuery(null, '/');
      return;
    }
    handleBack();
  };

  switch (currentTool) {
    case 'pan': {
      const searchParams = new URLSearchParams(window.location.search);
      const launchedFrom = !isArchiveToolActive ? String(searchParams.get('from') || '').trim() : '';
      const launchedFromWence = launchedFrom === 'wence' || launchedFrom === 'xiaoyaoWence';
      const returnTool = launchedFrom === 'xiaoyaoWence' ? 'xiaoyaoWence' : 'wence';
      const returnToWence = () => {
        const query = new URLSearchParams();
        query.set('from', 'pan');
        onEnterToolMode(returnTool, { queryParams: query });
      };
      return (
        <PanTestPage
          userProfile={userProfile}
          memberActive={memberActive}
          fourXiangyiVisible={fourXiangyiUnlocked}
          dockStyle={effectiveMobileDockStyle}
          xiangyiCategoryMode={xiangyiCategoryMode}
          onRequireAuth={() => setShowAuthModal(true)}
          onRequireProAccess={() => openProModal('AI 解盘')}
          onBack={launchedFromWence ? returnToWence : handleBack}
          onReturnToWence={launchedFromWence ? returnToWence : undefined}
          preferArchiveBack={isArchiveToolActive}
          pendingRecordId={archivePendingRecordId}
          onPendingRecordConsumed={(recordId) => onArchiveRecordConsumed?.('pan', recordId)}
          onOpenTool={(tool, queryParams) => {
            onEnterToolMode(tool, { fromPan: true, queryParams });
          }}
          onNavigateTab={(tab) => {
            if (tab === 'pan') {
              onSetViewMode('sidebar_view');
              onSetCurrentTool('pan');
              onSetReturnToPanFromTool(false);
              return;
            }
            onClearSelectedSymbol();
            onSetCurrentTool(null);
            onSetViewMode('dashboard');
            onSetMobileTab(tab);
            onSetReturnToPanFromTool(false);
            onReplaceCurrentUrlQuery(null, '/');
          }}
        />
      );
    }
    case 'dalurenPan':
      return (
        <DalurenPanPage
          onBack={handleBack}
          dockStyle={effectiveMobileDockStyle === 'ios' ? 'ios' : 'classic'}
          preferArchiveBack={isArchiveToolActive}
          pendingRecordId={archivePendingRecordId}
          onPendingRecordConsumed={(recordId) => onArchiveRecordConsumed?.('dalurenPan', recordId)}
          onNavigateTab={(tab) => {
            if (tab === 'pan') {
              onSetViewMode('sidebar_view');
              onSetCurrentTool('dalurenPan');
              onSetReturnToPanFromTool(false);
              return;
            }
            onClearSelectedSymbol();
            onSetCurrentTool(null);
            onSetViewMode('dashboard');
            onSetMobileTab(tab);
            onSetReturnToPanFromTool(false);
            onReplaceCurrentUrlQuery(null, '/');
          }}
        />
      );
    case 'trainer':
      return <Trainer onBack={handleBack} intro={buildIntro('trainer')} />;
    case 'decoder':
      return <Decoder onBack={handlePracticeBack} intro={buildIntro('decoder')} />;
    case 'findObject':
      return <FindObject onBack={handlePracticeBack} intro={buildIntro('findObject')} />;
    case 'quiz':
      return <Quiz onBack={handleBack} intro={buildIntro('quiz')} />;
    case 'knowledgePK':
      return <KnowledgePK onBack={handleBack} intro={buildIntro('knowledgePK')} />;
    case 'sancaiLiuge':
      return (
        <SancaiLiuge
          onBack={handleBack}
          currentUser={userProfile}
          memberActive={memberActive}
          onRequestProAccess={() => openProModal('三才六格 AI 专业解析')}
          pendingRecordId={archivePendingRecordId}
          onPendingRecordConsumed={(recordId) => onArchiveRecordConsumed?.('sancaiLiuge', recordId)}
          onOpenKangxiDictionary={(char) => {
            void (async () => {
              if (!(await ensureTrialAvailable('kangxiDictionary', '康熙字典'))) return;
              onEnterToolMode('kangxiDictionary', {
                queryParams: new URLSearchParams({ char }),
              });
            })();
          }}
          initialSurname={(() => {
            const p = new URLSearchParams(window.location.search);
            return p.get('surname') || undefined;
          })()}
          initialGivenName={(() => {
            const p = new URLSearchParams(window.location.search);
            return p.get('givenName') || undefined;
          })()}
        />
      );
    case 'kangxiDictionary':
      return (() => {
        const p = new URLSearchParams(window.location.search);
        const launchedFrom = String(p.get('from') || '').trim();
        const returnToNamingMaster = () => {
          const query = new URLSearchParams();
          const surname = String(p.get('surname') || '').trim();
          const combo = String(p.get('combo') || '').trim();
          const view = String(p.get('view') || '').trim();
          const gender = String(p.get('gender') || '').trim();
          if (surname) query.set('surname', surname);
          if (combo) query.set('combo', combo);
          if (view === 'candidates' || view === 'saved') query.set('view', view);
          if (gender === 'female' || gender === 'male') query.set('gender', gender);
          onEnterToolMode('namingMaster', { queryParams: query });
        };
        return (
          <KangxiDictionary
            onBack={launchedFrom === 'namingMaster' ? returnToNamingMaster : handleBack}
            onOpenSancaiLiuge={(char) => {
              if (!ensureTrialAvailable('sancaiLiuge', '三才六格')) return;
              onEnterToolMode('sancaiLiuge', {
                queryParams: new URLSearchParams({ surname: char }),
              });
            }}
          />
        );
      })();
    case 'namingMaster':
      return (
        <NamingMasterApp
          onBack={handleBack}
          onOpenSancaiLiuge={(char) => {
            void (async () => {
              if (!(await ensureTrialAvailable('sancaiLiuge', '三才六格'))) return;
              onEnterToolMode('sancaiLiuge', {
                queryParams: new URLSearchParams({ surname: char }),
              });
            })();
          }}
        />
      );
    case 'wanNianLi':
      return <WanNianLiApp onBack={handleBack} />;
    case 'wuxingClothing':
      return <WuxingClothing onBack={handleBack} />;
    case 'wence':
      return (
        <WenceApp
          onBack={handleBack}
          onOpenPan={(seedQuestion) => {
            const query = new URLSearchParams();
            query.set('from', 'wence');
            const normalized = String(seedQuestion || '').trim();
            if (normalized) query.set('question', normalized);
            onEnterToolMode('pan', { queryParams: query });
          }}
        />
      );
    case 'xiaoyaoWence':
      return (
        <WenceApp
          appLabel="逍遥问测"
          daoName="逍遥道长"
          featureId="xiaoyaoWence"
          storageNamespace="xiaoyao_wence"
          onBack={handleBack}
          onOpenPan={(seedQuestion) => {
            const query = new URLSearchParams();
            query.set('from', 'xiaoyaoWence');
            const normalized = String(seedQuestion || '').trim();
            if (normalized) query.set('question', normalized);
            onEnterToolMode('pan', { queryParams: query });
          }}
        />
      );
    case 'zhengyuanReport':
      return <ZhengYuanReportPage onBack={handleBack} />;
    case 'theory':
      return <TheoryReader onBack={handleBack} intro="系统梳理阴盘奇门基础到实战章节，先学第一章，再按目录逐章推进。" canAccessPro={theoryProAccess} />;
    case 'xuanxueStories':
      return <StoryReader onBack={handleBack} />;
    case 'originMemory':
      return (
        <OriginMemoryReader
          onBack={handleBack}
          canAccessPro={originMemoryProAccess}
          proCategoryUnlocks={originMemoryProCategoryUnlocks}
          onRequirePro={(toolName) => openProModal(toolName || '象意速记')}
        />
      );
    default: {
      const PsychToolPage = PSYCH_TOOL_PAGE_MAP[currentTool];
      if (PsychToolPage) {
        return <PsychToolPage onBack={handleBack} onRequireLogin={() => setShowAuthModal(true)} />;
      }
      return null;
    }
  }
};

export default AppToolRenderer;
